import {
  prepareVotingStorage,
  syncVotingFormData,
  tagProjectFormRecords,
  getPreviousSubmissionCandidates,
  getDefaultVotingResetPolicy,
} from '../../utils/voteRollover.js'

// Dependency injection permits ordering/failure tests without running an SDK,
// accessing a real browser, or making requests to Monterosa.
export const createVotingInitializer = ({
  configure,
  getSdks,
  getProject,
  getEvents,
  getEvent,
  getElements,
  onEventAdded,
  getStorage,
}) => {
  return async function init(host, projectId, onEventUpdate, options = {}) {
    const scope = {
      host,
      projectId,
      season: options.season,
      environment: options.environment,
      origin: typeof window !== 'undefined' ? window.location.origin : null,
    }
    const storage = getStorage()
    const locks = typeof navigator !== 'undefined' ? navigator.locks : null
    // Runs before configure, history listeners, or the first cached answer read.
    const migrationResult = await prepareVotingStorage({
      storage,
      scope,
      policy: options.resetPolicy || getDefaultVotingResetPolicy(scope),
      hasConfiguredSdk: getSdks().length > 0,
      withLock: locks?.request
        ? (name, action) => locks.request(name, { mode: 'exclusive' }, action)
        : null,
    })
    // SDK 0.18.10 reuses an existing instance by name, even if options change.
    const sdk = configure({ host, projectId }, `aa:${host}:${projectId}`)
    try {
      const project = await getProject(sdk)
      if (project.id !== projectId || project.host !== host) {
        throw new Error('Unexpected Monterosa project or host')
      }
      let events = await getEvents(project)
      let closed = false
      const assertProject = (event) => {
        if (
          event &&
          (event.context?.project?.id !== projectId ||
            event.context?.project?.host !== host)
        ) {
          throw new Error('Unexpected Monterosa event ownership')
        }
      }
      const hydrateEvent = async (summary) => {
        const event = summary ? await getEvent(summary.id, project) : null
        assertProject(event)
        // Adoption is based on project-owned event metadata, never a timestamp.
        tagProjectFormRecords({
          storage,
          scope,
          events: [...events, event].filter(Boolean),
        })
        const rolloverResult = syncVotingFormData({
          storage,
          meta: {
            ...scope,
            eventId: event?.id,
            currentFormId: event?.fields?.formId?.data?.[0]?.id,
          },
        })
        const elements = event ? await getElements(event) : []
        let oldEvent = null
        let oldElements = []
        if (event) {
          for (const candidate of getPreviousSubmissionCandidates({
            storage,
            scope,
            activeEventId: event.id,
          })) {
            // getEvent has an origin-wide event cache: validate the returned context.
            const previous = await getEvent(candidate.eventId, project)
            if (
              !previous ||
              previous.context?.project?.id !== projectId ||
              previous.context?.project?.host !== host ||
              previous.startAt >= event.startAt ||
              String(previous.fields?.formId?.data?.[0]?.id) !==
                candidate.currentFormId
            )
              continue
            oldEvent = previous
            oldElements = await getElements(previous)
            break
          }
        }
        return { event, elements, oldEvent, oldElements, rolloverResult }
      }
      const sortedEvents = [...events].sort((a, b) => b.startAt - a.startAt)
      const first = await hydrateEvent(
        sortedEvents.find((event) => event.state === 'active')
      )
      let updates = Promise.resolve()
      const unsubscribe = onEventAdded(project, (newEvent) => {
        updates = updates
          .then(async () => {
            if (closed) return
            assertProject(newEvent)
            events = [
              ...events.filter((item) => item.id !== newEvent.id),
              newEvent,
            ].sort((a, b) => b.startAt - a.startAt)
            const refreshed = events.find((event) => event.state === 'active')
            if (!refreshed) return
            const next = await hydrateEvent(refreshed)
            if (!closed)
              onEventUpdate?.(next.event, next.elements, {
                oldEvent: next.oldEvent,
                oldElements: next.oldElements,
                rolloverResult: next.rolloverResult,
              })
          })
          .catch((error) =>
            console.error('Monterosa event refresh failed:', error)
          )
      })
      return {
        sdk,
        project,
        activeEvent: first.event,
        events: sortedEvents,
        elements: first.elements,
        oldEvent: first.oldEvent,
        oldElements: first.oldElements,
        rolloverResult: first.rolloverResult,
        migrationResult,
        unsubscribeOnEventAdded: () => {
          closed = true
          unsubscribe()
        },
      }
    } catch (error) {
      console.error('Error initializing Monterosa SDK:', error)
      throw error
    }
  }
}
